import { sortBy } from "lodash-es";

import { cardActions } from "@/action/cardActions";
import { dragActions } from "@/action/dragActions";
import type { Drag, DragHandlers } from "@/components/gestures/drag";
import { sendStickyNoteAction } from "@/composables/useEventBus";
import { captureMessage } from "@/error/sentry";
import {
  asLen,
  boardFromWindow,
  boardScaleFromWindow,
  relativeFromBoard,
  windowScaleFromBoard,
} from "@/math/coordinate-transform";
import { relativeFromViewport } from "@/math/coordinate-utils";
import { minus, plus, times } from "@/math/coordinates";
import type { Card } from "@/model/card";
import type { BoardCoordinate, WindowLen } from "@/model/coordinates";
import { useBoardStore } from "@/store/board";
import { useDraggingStore } from "@/store/dragging";
import { useZoomStore } from "@/store/zoom";
import { clientCoord, elemOffsetCoord } from "@/utils/dom/coordinates";

interface CardInfo {
  id: string;
  el: HTMLElement;
  offset: WindowLen;
}

export function stickyDragHandler(e: PointerEvent, card: Card, onEnd: () => void): DragHandlers {
  let selectedCards: CardInfo[] = null!;

  return {
    start(drag) {
      // Handle cards dragged from outside (issue selector, palette, etc.) vs normal board drags
      const { grabFromOutside } = useDraggingStore().initCardDrag(card);

      // grab-from-outside: offset zero -> drag at sticky center
      // Normal: drag sticky at the point the mouse was starting the drag
      drag.setOffset(grabFromOutside ? "zero" : "elRelative");
      selectedCards = cardsToDrag(card, drag);

      const start = selectedCards.map((card) => {
        const offset = times(card.offset, useZoomStore().factor);
        const dragDistance = minus(clientCoord(e), asLen(drag.offset));
        const pos = relativeFromViewport(plus(dragDistance, offset));
        return { id: card.id, pos };
      });
      dragActions.startCardDrag("mouse", start);
      cardActions.setPosition(
        "board",
        selectedCards.map((c) => c.id),
      );
    },

    move(drag) {
      const pos = boardFromWindow(drag.pos, "output", selectedCards);
      for (const card of selectedCards) {
        dragActions.dragCard("internal", card.id, relativeFromBoard(cardCoordinate(card, pos)));
      }
      return pos;
    },

    stop(drag) {
      const dragInfo = useDraggingStore().dragging[selectedCards[0].id];
      if (dragInfo.autoFocus) {
        setTimeout(() => {
          sendStickyNoteAction(card.id, {
            action: "enlarge",
            focus: true,
            trigger: "after-create",
          });
        }, 100);
      }
      const pos = boardFromWindow(drag.pos, "output", selectedCards);
      const end = selectedCards.map((card) => ({
        id: card.id,
        pos: relativeFromBoard(cardCoordinate(card, pos)),
      }));
      dragActions.endCardDrag("mouse", end);
      onEnd();
      return pos;
    },
  };
}

function cardsToDrag(card: Card, drag: Drag) {
  const selected = useBoardStore().selectedStickyIds;
  const availableSelectedCards =
    selected.length > 0 && useBoardStore().isStickySelected(card.id)
      ? selected.map((id) => selectedCardInfo(id, drag))
      : [selectedCardInfo(card.id, drag)];
  const board = useBoardStore().currentBoard();
  // sort multiselected cards by z-index to keep their relative z-positions when moving them to the front
  return sortBy(
    availableSelectedCards.filter((card) => card !== null),
    (info) => {
      const cardOnBoard = board.cards[info.id];
      if (cardOnBoard) {
        return cardOnBoard.meta.zIndex;
      }
      captureMessage("Drag error - selected card not found on board", {
        info: { boardId: board.id, cardId: info.id, availableSelectedCards },
      });
      return 0;
    },
  );
}

function selectedCardInfo(cardId: string, drag: Drag): CardInfo | null {
  const el = document.getElementById(cardId);
  return el ? { id: cardId, el, offset: cardOffset(el, drag) } : null;
}

function cardOffset(el: HTMLElement, drag: Drag) {
  return windowScaleFromBoard(
    minus(elemOffsetCoord<BoardCoordinate>(el), elemOffsetCoord<BoardCoordinate>(drag.el)),
  );
}

function cardCoordinate(card: CardInfo, pos: BoardCoordinate) {
  return plus(pos, boardScaleFromWindow(card.offset));
}
